import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRightIcon, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";

export default function AccountSetup() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    jobTitle: "",
    accountType: "",
    bio: "",
    location: "",
    agreeTerms: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Redirect if not authenticated
  useEffect(() => {
    let isMounted = true;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    const loadProfile = async () => {
      if (!user?.id || !isMounted) return;
      
      try {
        setIsLoadingProfile(true);
        
        const { data: profileData, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id);
        
        if (!isMounted) return;
        
        if (error) {
          console.error('Error loading profile:', error);
        } else if (profileData && profileData.length > 0) {
          const profile = profileData[0];
          // Split full_name into first and last name if available
          let firstName = "";
          let lastName = "";
          
          if (profile.full_name && typeof profile.full_name === 'string') {
            const nameParts = profile.full_name.split(' ');
            firstName = nameParts[0] || "";
            lastName = nameParts.slice(1).join(' ') || "";
          }
          
          setFormData({
            firstName,
            lastName,
            companyName: typeof profile.company_name === 'string' ? profile.company_name : "",
            jobTitle: typeof profile.job_title === 'string' ? profile.job_title : "",
            accountType: typeof profile.user_type === 'string' ? profile.user_type : "",
            bio: "", // Bio field doesn't exist in the profile table
            location: [profile.city, profile.state, profile.country]
              .filter((item): item is string => typeof item === 'string')
              .join(", "),
            agreeTerms: true, // Assume agreed if they already have a profile
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error('Unexpected error loading profile:', err);
        }
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };
    
    loadProfile();
    
    return () => {
      isMounted = false;
    };
  }, [user, navigate]);
  


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user selects
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));

    // Clear error when user checks
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.accountType) {
      newErrors.accountType = "Please select an account type";
    }

    if (!formData.location.trim()) {
      newErrors.location = "Location is required";
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = "You must agree to the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user?.id) {
      return;
    }

    setIsLoading(true);

    try {
      // Extract location data
      const locationParts = formData.location.split(',').map(part => part.trim());
      const city = locationParts[0] || '';
      const state = locationParts[1] || '';
      const country = locationParts[2] || 'India'; // Default to India if not specified
      
      // Prepare full name
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // Update the profile in Supabase
      const { error } = await supabase
        .from('accounts')
        .update({
          full_name: fullName,
          company_name: formData.companyName,
          job_title: formData.jobTitle,
          user_type: formData.accountType,
          city,
          state,
          country,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) {
        console.error('Error updating profile:', error);
        throw new Error(error.message);
      }
      
      // Refresh user data
      await refreshUser();
      
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting profile:', err);
      setErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Account Setup", href: "/account-setup" }
  ];
  
  // Show loading state while fetching profile
  if (isLoadingProfile) {
    return (
      <div className="container max-w-screen-xl mx-auto px-4 py-12">
        <BreadcrumbNavigation items={breadcrumbItems} />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="container max-w-screen-xl mx-auto px-4 py-12">
        {/* Breadcrumb Navigation */}
        <BreadcrumbNavigation items={breadcrumbItems} />
        <div className="flex flex-col items-center justify-center">
          <div className="w-full max-w-md mb-8 text-center">
            <div className="bg-green-100 dark:bg-green-900 p-6 rounded-lg mb-6">
              <h1 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2">
                Account Setup Complete!
              </h1>
              <p className="text-green-700 dark:text-green-300">
                Your account has been successfully created and your profile is
                ready.
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full"
              aria-label="Navigate to homepage after completing account setup"
            >
              Go to Homepage
            </Link>
              {errors.submit && (
                <p className="text-sm text-red-500 mt-4">{errors.submit}</p>
              )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-screen-xl mx-auto px-4 py-12">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation items={breadcrumbItems} />
      
      <div className="flex flex-col items-center justify-center">
        <div className="w-full max-w-2xl mb-8">
          <h1 className="text-3xl font-bold text-center mb-2">
            Complete Your Account
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            Tell us a bit about yourself to get started
          </p>

          <Card>
            <form onSubmit={handleSubmit}>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Please provide your details to complete your registration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? "border-red-500" : ""}
                    />

                    {errors.firstName && (
                      <p className="text-sm text-red-500">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? "border-red-500" : ""}
                    />

                    {errors.lastName && (
                      <p className="text-sm text-red-500">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name (Optional)</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title (Optional)</Label>
                  <Input
                    id="jobTitle"
                    name="jobTitle"
                    placeholder="e.g., Procurement Manager, Buyer, etc."
                    value={formData.jobTitle}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accountType">Account Type</Label>
                  <Select
                    value={formData.accountType}
                    onValueChange={(value) =>
                      handleSelectChange("accountType", value)
                    }
                  >
                    <SelectTrigger
                      id="accountType"
                      className={errors.accountType ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer</SelectItem>
                      <SelectItem value="both">Both (Buy & Sell)</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.accountType && (
                    <p className="text-sm text-red-500">{errors.accountType}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="City, State, Country"
                    value={formData.location}
                    onChange={handleChange}
                    className={errors.location ? "border-red-500" : ""}
                  />

                  {errors.location && (
                    <p className="text-sm text-red-500">{errors.location}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio (Optional)</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    placeholder="Tell us a bit about yourself or your business"
                    value={formData.bio}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreeTerms"
                    checked={formData.agreeTerms}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("agreeTerms", checked === true)
                    }
                    className={errors.agreeTerms ? "border-red-500" : ""}
                  />

                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="agreeTerms"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      I agree to the Terms of Service and Privacy Policy
                    </label>
                    {errors.agreeTerms && (
                      <p className="text-sm text-red-500">
                        {errors.agreeTerms}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      <span>Processing...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span>Complete Registration</span>
                      <ArrowRightIcon className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
} 